import { useCallback, useEffect, useState } from 'react';
import { upi, rupees, toPaise, INSTRUMENT_LABELS, type Attestation, type InstrumentKind } from '../../api/upi';
import { formatDate, shortDid } from './terms';

interface Props {
  role: 'customer' | 'agent';
  did: string;
  mockBank?: string;
}

const input = 'mt-1 w-full rounded-lg border border-border-subtle px-3 py-2 text-xs';

/**
 * Bank instruments an agent can be paid from. The bank attests each one;
 * a mandate is bound to an attestation and stops when the bank withdraws
 * it. With the mock bank, the customer sets one up here as they would in
 * their bank's app.
 */
export default function InstrumentsTab({ role, did, mockBank }: Props) {
  const [agent, setAgent] = useState(role === 'agent' ? did : '');
  const [list, setList] = useState<Attestation[] | null>(null);
  const [error, setError] = useState('');
  const [kind, setKind] = useState<InstrumentKind>('upi-circle-full');
  const [perTxn, setPerTxn] = useState('');
  const [perDay, setPerDay] = useState('');
  const [block, setBlock] = useState('');
  const [busy, setBusy] = useState(false);

  // An operator sees the agent's instruments; a customer sees theirs with every agent.
  const load = useCallback(() => {
    setError('');
    (role === 'agent' ? upi.attestations(did) : upi.attestations(undefined, did))
      .then(setList)
      .catch((e) => { setList(null); setError(e instanceof Error ? e.message : String(e)); });
  }, [role, did]);
  useEffect(() => { load(); }, [load]);

  const setUp = async () => {
    setError('');
    const txn = toPaise(perTxn);
    const day = perDay.trim() ? toPaise(perDay) : undefined;
    const blk = kind === 'upi-reserve-pay' ? toPaise(block) : undefined;
    if (txn === null || day === null || blk === null) return setError('Amounts must be in rupees with at most two decimals.');
    const limits: Record<string, unknown> = { currency: 'INR', maxPerTxnPaise: txn };
    if (day !== undefined) limits.maxPerDayPaise = day;
    if (blk !== undefined) { limits.maxPerPeriodPaise = blk; limits.period = 'P1M'; }
    setBusy(true);
    try {
      await upi.mockAttest({
        kind, instrumentRef: `${kind}-${crypto.randomUUID().slice(0, 8)}`, customerDid: did, agentDid: agent.trim(), limits,
        validUntil: new Date(Date.now() + 90 * 864e5).toISOString().replace(/\.\d{3}Z$/, 'Z'),
      });
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (a: Attestation) => {
    if (!confirm('Remove this instrument at the bank? Every mandate bound to it stops.')) return;
    try {
      await upi.mockRemove(a.id, did);
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <div className="space-y-6">
      {error && <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>}

      {list && list.length === 0 && (
        <div className="rounded-xl border border-dashed border-border-subtle p-8 text-center text-sm text-text-muted">
          {role === 'customer' ? 'No bank has attested an instrument for you with any agent.' : 'No bank has attested an instrument for this agent.'}
        </div>
      )}
      {list?.map((a) => (
        <div key={a.id} className="rounded-xl border border-border-subtle bg-white p-5 shadow-sm">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="font-semibold text-text-main">{INSTRUMENT_LABELS[a.kind] ?? a.kind}</p>
              <p className="mt-0.5 font-mono text-[11px] text-text-muted">
                {a.instrumentRef} · bank {shortDid(a.bank)}{role === 'customer' && a.agent && <> · agent {shortDid(a.agent)}</>}
              </p>
            </div>
            <span className={`rounded-full px-2.5 py-1 text-[10px] font-bold uppercase ${a.usable ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
              {a.usable ? 'Usable' : 'Not usable'}
            </span>
          </div>
          <p className="mt-2 text-xs text-text-main">
            Bank limits: {rupees(a.limits.maxPerTxnPaise)} a payment
            {a.limits.maxPerDayPaise !== undefined && <>, {rupees(a.limits.maxPerDayPaise)} a day</>}
            {a.kind === 'upi-reserve-pay' && a.limits.maxPerPeriodPaise !== undefined && <>, {rupees(a.limits.maxPerPeriodPaise)} blocked</>}
          </p>
          <p className="mt-1 text-[11px] text-text-muted">Attested until {formatDate(a.validUntil)}</p>
          {a.problem && <p className="mt-1 text-[11px] text-red-700">{a.problem}</p>}
          {role === 'customer' && mockBank && a.bank === mockBank && a.usable && (
            <button onClick={() => void remove(a)} className="mt-3 rounded-lg border border-red-200 px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-50">
              Remove at the bank
            </button>
          )}
        </div>
      ))}

      {role === 'customer' && mockBank && (
        <section className="rounded-xl border border-border-subtle bg-white p-5 shadow-sm">
          <h3 className="text-sm font-bold text-text-main">Set up an instrument at the mock bank</h3>
          <p className="mt-1 text-xs text-text-muted">
            This stands in for adding the agent to your UPI Circle, or blocking funds for it, in your bank's app. The mock bank moves no money.
          </p>
          <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-4">
            <label className="block sm:col-span-4">
              <span className="text-xs font-semibold text-text-muted">Agent DID</span>
              <input id="instruments-agent" value={agent} onChange={(e) => setAgent(e.target.value)} placeholder="did:adi:agent:…" className={`${input} font-mono`} />
            </label>
            <label className="block sm:col-span-2">
              <span className="text-xs font-semibold text-text-muted">Kind</span>
              <select id="instrument-kind" value={kind} onChange={(e) => setKind(e.target.value as InstrumentKind)} className={input}>
                {(['upi-circle-full', 'upi-circle-partial', 'upi-reserve-pay'] as const).map((k) => <option key={k} value={k}>{INSTRUMENT_LABELS[k]}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-xs font-semibold text-text-muted">Per payment (₹)</span>
              <input id="instrument-per-txn" inputMode="decimal" value={perTxn} onChange={(e) => setPerTxn(e.target.value)} placeholder="5000" className={input} />
            </label>
            <label className="block">
              <span className="text-xs font-semibold text-text-muted">{kind === 'upi-reserve-pay' ? 'Amount blocked (₹)' : 'Per day (₹)'}</span>
              {kind === 'upi-reserve-pay'
                ? <input id="instrument-block" inputMode="decimal" value={block} onChange={(e) => setBlock(e.target.value)} placeholder="10000" className={input} />
                : <input id="instrument-per-day" inputMode="decimal" value={perDay} onChange={(e) => setPerDay(e.target.value)} placeholder="15000" className={input} />}
            </label>
          </div>
          <div className="mt-4 flex justify-end">
            <button onClick={() => void setUp()} disabled={busy || !agent.trim() || !perTxn.trim()} className="btn-primary disabled:opacity-50">
              {busy ? 'Setting up…' : 'Set up'}
            </button>
          </div>
        </section>
      )}
    </div>
  );
}
